/* eslint-disable max-len */
/**
 * RawDataGrid Component
 * 
 * A high-performance data grid for displaying billing cycles with advanced filtering and sorting.
 * 
 * @component
 * @example
 * ```tsx
 * <RawDataGrid
 *   selectedLines={contractLines}
 *   cyclesPerLine={10}
 *   columns={customColumns}
 *   onCycleAction={(cycle, action) => console.log(action, cycle)}
 * />
 * ```
 * 
 * @props
 * - selectedLines: ContractLine[] - Array of contract lines with billing cycles
 * - cyclesPerLine?: number - Optional limit on cycles to display per line
 * - columns?: ColumnConfig[] - Optional custom column configuration
 * - onCycleAction?: (cycle: BillingCycle, action: string) => void - Optional callback for cycle actions
 * - dateFormat?: string - Optional date format (default: "MM/dd/yyyy")
 * 
 * @performance
 * - Optimized filtering with useMemo
 * - Efficient sorting algorithms
 * - Minimal re-renders through useCallback
 * - Virtual scrolling ready structure
 */

import { useState, useMemo, useCallback, useRef } from 'react';

import { ArrowUpDown, ArrowUp, ArrowDown, Download, ExternalLink } from 'lucide-react';

import * as XLSX from 'xlsx';

import { format as formatDateFns } from 'date-fns';

import { ContractLine, BillingCycle } from './types';
import { UserRole } from '@/config/roles.config';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/evo/ui/card';
import { Button } from '@/components/evo/ui/button';
import { CycleActionButtons } from './CycleActionButtons';
import { ColumnFilter } from './ColumnFilter';
import { useColumnResize } from '@/hooks/evo/use-column-resize';
import { ResizeHandle } from '@/components/evo/ui/resize-handle';


import { DEFAULT_DATE_PATTERN_UNICODE } from '@/config/common.config';
import { browserHistory } from '@/routing/browserHistory';
import { getParameterizedLink } from '@/utils/getLinkWithSubscriptionId.utils';
import { billingBCPath } from '@/utils/path.utlis';

interface RawDataGridProps {
  selectedLines: ContractLine[];
  cyclesPerLine?: number;
  takeFromStart?: boolean;
  columns?: ColumnConfig[];
  onCycleAction?: (cycle: BillingCycle, action: string) => void;
  dateFormat?: string;
  onDataChanged?: () => void;
  onInvoiceCycle?: (cycle: BillingCycle) => void;
  userRole?: UserRole;
  readOnlyLineIds?: Set<string>;
}

type SortDirection = 'asc' | 'desc';
type SortConfigEntry = { column: keyof BillingCycle; direction: SortDirection };

interface ColumnFilterState {
  [key: string]: string;
}

interface ColumnConfig {
  key: keyof BillingCycle;
  label: string;
  width?: string;
  filterType: 'text' | 'number' | 'date' | 'select';
  selectOptions?: Array<{ value: string; label: string }>;
}

// Default column configuration - can be overridden via props
const DEFAULT_COLUMNS: ColumnConfig[] = [
  { key: 'stockCode', label: 'Stock Code', width: 'w-48', filterType: 'select' },
  { key: 'billingDate', label: 'Billing Date', width: 'w-28', filterType: 'date' },
  { key: 'startDate', label: 'BC Start Date', width: 'w-28', filterType: 'date' },
  { key: 'endDate', label: 'BC End Date', width: 'w-28', filterType: 'date' },
  {
    key: 'processingStatus',
    label: 'Processing Status',
    width: 'w-36',
    filterType: 'select',
    selectOptions: [
      { value: 'ActivePending', label: 'ActivePending' },
      { value: 'SoCreated', label: 'SoCreated' },
      { value: 'Excluded', label: 'Excluded' },
      { value: 'Ignored', label: 'Ignored' },
    ]
  },
  { key: 'statusReason', label: 'Status Reason', width: 'w-48', filterType: 'text' },
  { key: 'billingValue', label: 'Billing Value', width: 'w-28', filterType: 'number' },
  { key: 'contractLineID', label: 'Contract Line ID', width: 'w-64', filterType: 'select' },
  { key: 'latestSalesOrderId', label: 'Latest SO By BC ID', width: 'w-64', filterType: 'text' },
  { key: 'soCount', label: 'SO Count', width: 'w-36', filterType: 'number' },
];

export function RawDataGrid({
  selectedLines,
  cyclesPerLine,
  takeFromStart = false,
  columns = DEFAULT_COLUMNS,
  onDataChanged,
  onInvoiceCycle,
  userRole,
}: RawDataGridProps) {
  // State: Multi-sort configuration (default: ContractLineID asc, Start Date desc)
  const [sortConfig, setSortConfig] = useState<SortConfigEntry[]>([
    { column: 'contractLineID', direction: 'asc' },
    { column: 'startDate', direction: 'desc' },
  ]);

  // State: Filtering
  const [filters, setFilters] = useState<ColumnFilterState>({});

  // Ref for table element (mutable ref)
  const tableRef = useRef<HTMLTableElement | null>(null);

  // Column resize functionality
  const columnKeys = columns.map(col => col.key);
  const defaultWidths = columns.reduce((acc, col) => {
    acc[col.key] = 150; // Default width for all columns
    return acc;
  }, {} as Record<string, number>);

  const { columnWidths, handleMouseDown, handleDoubleClick, setTableRef } = useColumnResize({
    tableId: 'raw-data-grid',
    columns: [...columnKeys, 'actions'],
    defaultWidths: {
      ...defaultWidths,
      actions: 200,
    },
    minWidth: 80,
    maxWidth: 600,
  });

  // Memoized: Flatten all billing cycles from selected lines
  const allCycles = useMemo(() => {
    const cycles: BillingCycle[] = [];
    selectedLines.forEach((line) => {
      let lineCycles: BillingCycle[];
      if (cyclesPerLine) {
        lineCycles = takeFromStart
          ? line.BillingCycles.slice(0, cyclesPerLine)
          : line.BillingCycles.slice(-cyclesPerLine);
      } else {
        lineCycles = line.BillingCycles;
      }
      cycles.push(...lineCycles);
    });
    return cycles;
  }, [selectedLines, cyclesPerLine, takeFromStart]);

  // Memoized: Compute unique stockCode options from current data
  const stockCodeOptions = useMemo(() => {
    const map = new Map<string, string>();
    allCycles.forEach(cycle => {
      if (cycle.stockCode) {
        const code = String(cycle.stockCode);
        if (!map.has(code)) {
          map.set(code, `${code} - ${cycle.stockName || ''}`);
        }
      }
    });
    return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([value, label]) => ({ value, label }));
  }, [allCycles]);

  // Memoized: Compute unique contractLineID options from current data
  const contractLineIdOptions = useMemo(() => {
    const set = new Set<string>();
    allCycles.forEach(cycle => {
      if (cycle.contractLineID) {
        set.add(String(cycle.contractLineID));
      }
    });
    return Array.from(set).sort().map(value => ({ value, label: value }));
  }, [allCycles]);

  // Memoized: Apply filters and sorting
  const filteredAndSortedCycles = useMemo(() => {
    let result = [...allCycles];

    // Apply filters
    Object.entries(filters).forEach(([column, filterValue]) => {
      if (filterValue) {
        result = result.filter((cycle) => {
          const cellValue = cycle[column as keyof BillingCycle];
          const cellStr = String(cellValue || '').toLowerCase();

          // Parse filter format: "value|condition" or "value1|value2|between/range" or "value1|value2|...|multiselect"
          const parts = filterValue.split('|');

          // Handle multi-select filter (for enum fields)
          if (parts[parts.length - 1] === 'multiselect') {
            const selectedValues = parts.slice(0, -1).map(v => v.toLowerCase());
            return selectedValues.includes(cellStr);
          }

          if (parts.length === 1) {
            // Simple text filter (backward compatibility)
            return cellStr.includes(filterValue.toLowerCase());
          }

          const [value1, value2OrCondition, condition] = parts;
          const actualCondition = condition || value2OrCondition;

          // Handle date filters
          if (column === 'billingDate' || column === 'startDate' || column === 'endDate') {
            const cellDate = new Date(cellValue as string);
            const filterDate1 = new Date(value1);

            if (actualCondition === 'range') {
              const filterDate2 = new Date(value2OrCondition);
              return cellDate >= filterDate1 && cellDate <= filterDate2;
            } else if (actualCondition === 'before') {
              return cellDate < filterDate1;
            } else if (actualCondition === 'after') {
              return cellDate > filterDate1;
            } else if (actualCondition === 'equals') {
              return cellDate.toDateString() === filterDate1.toDateString();
            }
          }

          // Handle number filters
          if (column === 'billingValue') {
            const cellNum = Number(cellValue);
            const filterNum1 = Number(value1);

            if (actualCondition === 'between') {
              const filterNum2 = Number(value2OrCondition);
              return cellNum >= filterNum1 && cellNum <= filterNum2;
            } else if (actualCondition === 'greaterThan') {
              return cellNum > filterNum1;
            } else if (actualCondition === 'lessThan') {
              return cellNum < filterNum1;
            } else if (actualCondition === 'equals') {
              return cellNum === filterNum1;
            }
          }

          // Handle text filters
          const filterStr = value1.toLowerCase();
          if (actualCondition === 'contains') {
            return cellStr.includes(filterStr);
          } else if (actualCondition === 'equals') {
            return cellStr === filterStr;
          } else if (actualCondition === 'startsWith') {
            return cellStr.startsWith(filterStr);
          } else if (actualCondition === 'endsWith') {
            return cellStr.endsWith(filterStr);
          }

          return true;
        });
      }
    });

    // Apply multi-sort
    if (sortConfig.length > 0) {
      result.sort((a, b) => {
        for (const { column, direction } of sortConfig) {
          const aValue = a[column];
          const bValue = b[column];

          // Handle nulls
          if (aValue == null && bValue == null) continue;
          if (aValue == null) return 1;
          if (bValue == null) return -1;

          let comparison = 0;

          // Special handling for dates
          if (column === 'billingDate' || column === 'startDate' || column === 'endDate') {
            const aTime = Date.parse(String(aValue));
            const bTime = Date.parse(String(bValue));
            if (Number.isNaN(aTime) && Number.isNaN(bTime)) continue;
            if (Number.isNaN(aTime)) return 1;
            if (Number.isNaN(bTime)) return -1;
            comparison = aTime - bTime;
          } else if (column === 'billingValue') {
            // Special handling for numbers
            comparison = Number(aValue) - Number(bValue);
          } else {
            // Default string comparison
            comparison = String(aValue).localeCompare(String(bValue));
          }

          if (comparison !== 0) {
            return direction === 'asc' ? comparison : -comparison;
          }
        }
        return 0;
      });
    }

    return result;
  }, [allCycles, filters, sortConfig]);

  // Optimized handlers with useCallback - single state update for multi-sort
  const handleSort = useCallback((column: keyof BillingCycle) => {
    setSortConfig(prev => {
      const index = prev.findIndex(entry => entry.column === column);

      // Not in config yet → add as primary with asc
      if (index === -1) {
        return [{ column, direction: 'asc' as SortDirection }, ...prev];
      }

      const existing = prev[index];

      // asc → desc
      if (existing.direction === 'asc') {
        const next = [...prev];
        next[index] = { ...existing, direction: 'desc' as SortDirection };
        return next;
      }

      // desc → remove (no sort on this column)
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const handleFilterChange = useCallback((column: string, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [column]: value,
    }));
  }, []);

  // Utility functions - show sort direction and index for multi-sort
  const getSortIcon = useCallback((column: keyof BillingCycle) => {
    const index = sortConfig.findIndex(entry => entry.column === column);
    if (index === -1) {
      return <ArrowUpDown className="h-4 w-4 stroke-[2]" />;
    }

    const { direction } = sortConfig[index];

    return (
      <div className="flex items-center gap-0.5">
        {direction === 'asc' ? (
          <ArrowUp className="h-4 w-4 stroke-[2]" />
        ) : (
          <ArrowDown className="h-4 w-4 stroke-[2]" />
        )}
        <span className="text-[10px] text-muted-foreground font-medium">{index + 1}</span>
      </div>
    );
  }, [sortConfig]);

  const formatDate = useCallback((dateString: string) => {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '';
    return formatDateFns(date, DEFAULT_DATE_PATTERN_UNICODE); // dd/MM/yyyy
  }, []);

  // Export to Excel function
  const handleExportToExcel = useCallback(() => {
    // Prepare data for export - use filtered and sorted cycles
    const exportData = filteredAndSortedCycles.map((cycle) => {
      const row: any = {};

      // Add columns in the same order as displayed
      columns.forEach((column) => {
        const value = cycle[column.key];

        // Format stock code with name
        if (column.key === 'stockCode') {
          row[column.label] = `${cycle.stockCode} - ${cycle.stockName}`;
        }
        // Format dates
        else if (column.key === 'billingDate' || column.key === 'startDate' || column.key === 'endDate') {
          row[column.label] = formatDate(value as string);
        }
        // Format billing value
        else if (column.key === 'billingValue') {
          const formatted = (value as number).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
          row[column.label] = `${cycle.currency} ${formatted}`;
        }
        // Handle null values
        else if (value === null || value === undefined) {
          row[column.label] = '-';
        }
        // Default
        else {
          row[column.label] = value;
        }
      });

      return row;
    });

    // Create worksheet
    const worksheet = XLSX.utils.json_to_sheet(exportData);

    // Set column widths for Excel export
    const excelColumnWidths = columns.map((column) => ({
      wch: column.label.length + 5, // Add some padding
    }));
    worksheet['!cols'] = excelColumnWidths;

    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Billing Cycles');

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    const filename = `billing-cycles-${timestamp}.xlsx`;

    // Download file
    XLSX.writeFile(workbook, filename);
  }, [filteredAndSortedCycles, columns, formatDate]);

  // Early return for empty state
  if (selectedLines.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          No contract lines selected
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Billing Cycles Data</CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportToExcel}
            className="gap-2"
          >
            <Download className="h-4 w-4" />
            Export to Excel
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border overflow-hidden">
          <div className="relative w-full">
            <table ref={(el) => {
              tableRef.current = el;
              setTableRef(el);
            }} className="w-full caption-bottom text-sm table-fixed">
              <thead className="[&_tr]:border-b bg-background sticky top-0 z-10">
                <tr className="border-b transition-colors">
                  {columns.map((column) => (
                    <th
                      key={column.key}
                      className="text-foreground px-2 text-left align-middle font-medium
                        relative group bg-white dark:bg-gray-950"
                      style={{
                        width: columnWidths[column.key] || 150,
                        maxWidth: columnWidths[column.key] || 150,
                      }}
                    >
                      <div className="flex items-center h-10">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 flex-1 justify-between px-2 min-w-0"
                          onClick={() => handleSort(column.key)}
                        >
                          <span className="font-semibold truncate">{column.label}</span>
                          {getSortIcon(column.key)}
                        </Button>
                        <ColumnFilter
                          columnKey={column.key}
                          columnLabel={column.label}
                          filterType={column.filterType}
                          value={filters[column.key] || ''}
                          onFilterChange={(value) => handleFilterChange(column.key, value)}
                          selectOptions={
                            column.key === 'stockCode'
                              ? stockCodeOptions
                              : column.key === 'contractLineID'
                                ? contractLineIdOptions
                                : column.selectOptions
                          }
                        />
                      </div>
                      <ResizeHandle
                        onMouseDown={(e) => handleMouseDown(e, column.key)}
                        onDoubleClick={(e) => handleDoubleClick(e, column.key, tableRef.current)}
                      />
                    </th>
                  ))}
                  {/* Actions Column Header */}
                  <th
                    className="text-foreground px-2 text-left align-middle font-medium
                      relative group bg-white dark:bg-gray-950"
                    style={{
                      width: columnWidths.actions || 200,
                      maxWidth: columnWidths.actions || 200,
                    }}
                  >
                    <div className="h-10 flex items-center px-2">
                      <span className="font-semibold">Actions</span>
                    </div>
                    <ResizeHandle
                      onMouseDown={(e) => handleMouseDown(e, 'actions')}
                      onDoubleClick={(e) => handleDoubleClick(e, 'actions', tableRef.current)}
                    />
                  </th>
                </tr>
              </thead>
            </table>
          </div>

          {/* Scrollable Body */}
          <div className="max-h-[600px] overflow-y-auto">
            <table className="w-full caption-bottom text-sm table-fixed">
              <tbody className="[&_tr:last-child]:border-0">
                {filteredAndSortedCycles.length === 0 ? (
                  <tr className="hover:bg-muted/50 border-b transition-colors">
                    <td
                      colSpan={columns.length + 1}
                      className="p-2 align-middle whitespace-nowrap text-center text-muted-foreground"
                    >
                      No billing cycles found
                    </td>
                  </tr>
                ) : (
                  filteredAndSortedCycles.map((cycle, index) => (
                    <tr key={index} className="hover:bg-muted/50 border-b transition-colors">
                      <td
                        className="p-2 align-middle font-mono text-xs bg-white dark:bg-gray-950 overflow-hidden"
                        style={{
                          width: columnWidths[columns[0].key] || 150,
                          maxWidth: columnWidths[columns[0].key] || 150,
                        }}
                      >
                        <div className="truncate">{cycle.stockCode} - {cycle.stockName}</div>
                      </td>
                      <td
                        className="p-2 align-middle text-xs bg-white dark:bg-gray-950 overflow-hidden"
                        style={{
                          width: columnWidths[columns[1].key] || 150,
                          maxWidth: columnWidths[columns[1].key] || 150,
                        }}
                      >
                        <div className="truncate">{formatDate(cycle.billingDate)}</div>
                      </td>
                      <td
                        className="p-2 align-middle text-xs bg-white dark:bg-gray-950 overflow-hidden"
                        style={{
                          width: columnWidths[columns[2].key] || 150,
                          maxWidth: columnWidths[columns[2].key] || 150,
                        }}
                      >
                        <div className="truncate">{formatDate(cycle.startDate)}</div>
                      </td>
                      <td 
                        className="p-2 align-middle text-xs bg-white dark:bg-gray-950 overflow-hidden"
                        style={{
                          width: columnWidths[columns[3].key] || 150,
                          maxWidth: columnWidths[columns[3].key] || 150,
                        }}
                      >
                        <div className="truncate">{formatDate(cycle.endDate)}</div>
                      </td>
                      <td
                        className="p-2 align-middle bg-white dark:bg-gray-950 overflow-hidden"
                        style={{
                          width: columnWidths[columns[4].key] || 150,
                          maxWidth: columnWidths[columns[4].key] || 150,
                        }}
                      >
                        <div className="truncate">
                          <span
                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cycle.processingStatus === 'SoCreated'
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                              : cycle.processingStatus === 'Excluded'
                                ? 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                                : cycle.processingStatus === 'ActivePending'
                                  ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                  : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                            }`}
                          >
                            {cycle.processingStatus}
                          </span>
                        </div>
                      </td>
                      <td
                        className="p-2 align-middle text-xs bg-white dark:bg-gray-950 overflow-hidden"
                        style={{
                          width: columnWidths[columns[5].key] || 150,
                          maxWidth: columnWidths[columns[5].key] || 150,
                        }}
                      >
                        <div className="truncate">{cycle.statusReason || '-'}</div>
                      </td>
                      <td
                        className="p-2 align-middle text-xs text-right bg-white dark:bg-gray-950 overflow-hidden"
                        style={{
                          width: columnWidths[columns[6].key] || 150,
                          maxWidth: columnWidths[columns[6].key] || 150,
                        }}
                      >
                        <div className="truncate">
                          {cycle.currency} {cycle.billingValue.toLocaleString(
                            'en-US',
                            { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                          )}
                        </div>
                      </td>
                      <td
                        className="p-2 align-middle font-mono text-xs bg-white dark:bg-gray-950 overflow-hidden"
                        style={{
                          width: columnWidths[columns[7].key] || 150,
                          maxWidth: columnWidths[columns[7].key] || 150,
                        }}
                      >
                        <div className="truncate">{cycle.contractLineID}</div>
                      </td>
                      <td
                        className="p-2 align-middle font-mono text-xs bg-white dark:bg-gray-950 overflow-hidden"
                        style={{
                          width: columnWidths[columns[8]?.key] || 150,
                          maxWidth: columnWidths[columns[8]?.key] || 150,
                        }}
                      >
                        <div className="truncate">{cycle.latestSalesOrderId || '-'}</div>
                      </td>
                      <td
                        className="p-2 align-middle text-xs bg-white dark:bg-gray-950 overflow-hidden"
                        style={{
                          width: columnWidths[columns[9]?.key] || 150,
                          maxWidth: columnWidths[columns[9]?.key] || 150,
                        }}
                      >
                        <div className="flex items-center gap-1">
                          <span>{cycle.soCount ?? 0}</span>
                          {cycle.soCount != null && cycle.soCount > 0 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-5 w-5 p-0"
                              onClick={() => {
                                const link = `/${billingBCPath}?billingCycleId=${cycle.billingCycleID}`;
                                browserHistory.push(getParameterizedLink(link));
                              }}
                              title="View Sales Orders"
                            >
                              <ExternalLink className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </td>
                      <td
                        className="p-2 align-middle bg-white dark:bg-gray-950 overflow-hidden"
                        style={{ width: columnWidths.actions || 200, maxWidth: columnWidths.actions || 200 }}
                      >
                        <div className="truncate">
                          <CycleActionButtons cycle={cycle} variant="compact" onDataChanged={onDataChanged} onInvoice={onInvoiceCycle} userRole={userRole} />
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-4 text-sm text-muted-foreground">
          Showing {filteredAndSortedCycles.length} of {allCycles.length} billing cycles
        </div>
      </CardContent>
    </Card>
  );
}
